"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { BoxPreview } from "@/components/tools/word-games/box-preview";
import { useWordList } from "@/components/tools/word-games/use-word-list";
import { WordListStatus } from "@/components/tools/word-games/word-list-status";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type Box,
  type BoxWord,
  type Chain,
  type ChainSearch,
  findChains,
  MAX_PER_SIDE,
  parseBox,
  validWords,
} from "@/lib/tool-kit/word-games/letter-boxed";

/** English strings. A translated content file overrides these through `ui`. */
export const LETTER_BOXED_SOLVER_STRINGS = {
  sideLabels: ["Top side", "Right side", "Bottom side", "Left side"],
  sideHint: "Up to {max} letters, any order.",
  sidePlaceholder: "abc",
  startLabel: "Start with this word (optional)",
  startHint:
    "Chains begin with it, and the next word starts with its last letter.",
  startPlaceholder: "solidify",
  startInvalid:
    "“{word}” is not a valid word for this box: it needs three or more letters, only box letters, and no two neighboring letters from the same side.",
  tryExample: "Try an example",
  emptyPrompt:
    "Enter the letters on each side of the box to find valid words and the chains that cover every letter.",
  incomplete: "Fill in all four sides.",
  invalid: "Use the letters A–Z only.",
  duplicate: "Every letter in the box must be different.",
  tooMany: "A side holds at most {max} letters.",
  loading: "Loading the word list (once per visit)…",
  loadError:
    "Could not load the word list. Check your connection and try again.",
  retry: "Try again",
  heading: "Solutions",
  statValid: "Valid words",
  statOne: "One-word solutions",
  statTwo: "Two-word solutions",
  oneHeading: "One-word solutions",
  noOne: "No single word uses every letter.",
  twoHeading: "Two-word solutions",
  noTwo: "No two-word chain covers the box.",
  threeHeading: "Three-word chains",
  noThree: "No three-word chain was found.",
  findThree: "Find three-word chains",
  threeNote:
    "Three-word chains are searched with a cap, most efficient first, so a long list is a sample.",
  chainCount: "one:{count} chain|other:{count} chains",
  capped: "Showing the first {count} found; there may be more.",
  showMore: "Show more",
  copyAll: "Copy all",
  copied: "Copied!",
  topHeading: "Words that use the most letters",
  covers: "{count} of {total} letters",
  allHeading: "All valid words",
  showWords: "Show all valid words",
  hideWords: "Hide the word list",
  firstLetter: "Starting with {letter}",
  sourceNote:
    "Words come from ENABLE, a public-domain word-game list. It is not the list the puzzle uses, and it holds words of up to 15 letters, so the game may accept a few words this list does not and the reverse.",
} as const;

type Strings = typeof LETTER_BOXED_SOLVER_STRINGS;

const EXAMPLE = ["nod", "fms", "rel", "aiy"];
const SIDES = [0, 1, 2, 3] as const;
const PAGE = 40;
const TWO_LIMIT = 5000;
const THREE_LIMIT = 500;
const WORK_BUDGET = 20_000_000;
const RE_NON_LETTER = /[^a-z]/g;
const INVALID_KEYS = {
  invalid: "invalid",
  incomplete: "incomplete",
  duplicate: "duplicate",
  "too-many": "tooMany",
} as const;

function runSearch(
  words: readonly BoxWord[],
  box: Box,
  startWord: string,
  length: number,
  limit: number
): ChainSearch {
  return findChains(words, box, {
    length,
    limit,
    budget: WORK_BUDGET,
    start: startWord || undefined,
  });
}

function ChainList({
  heading,
  chains,
  total,
  truncated,
  empty,
  strings,
}: {
  heading: string;
  chains: readonly Chain[];
  total: number;
  truncated: boolean;
  empty: string;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [shown, setShown] = useState(PAGE);
  if (chains.length === 0) {
    return (
      <section className="space-y-2">
        <h3 className={WIDGET_H3}>{heading}</h3>
        <p className="text-muted-foreground text-sm">{empty}</p>
      </section>
    );
  }
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className={WIDGET_H3}>
          {heading}{" "}
          <span className={HINT}>
            {tPlural(strings.chainCount, locale, total, {
              count: fmt(total),
            })}
          </span>
        </h3>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyAll}
          small
          text={chains.map((chain) => chain.words.join(" > ")).join("\n")}
        />
      </div>
      <ul className="grid gap-2 sm:grid-cols-2">
        {chains.slice(0, shown).map((chain) => (
          <li
            className="rounded-md border border-border bg-card px-3 py-1.5 font-mono text-foreground text-sm"
            key={chain.words.join(">")}
          >
            {chain.words.join(" → ")}
          </li>
        ))}
      </ul>
      {chains.length > shown ? (
        <button
          className={BUTTON_SMALL}
          onClick={() => setShown((count) => count + PAGE)}
          type="button"
        >
          {strings.showMore}
        </button>
      ) : null}
      {truncated ? (
        <p className={HINT}>{t(strings.capped, { count: fmt(total) })}</p>
      ) : null}
    </section>
  );
}

function WordsByLetter({
  words,
  strings,
}: {
  words: readonly BoxWord[];
  strings: Strings;
}) {
  const groups = useMemo(() => {
    const byFirst = new Map<string, string[]>();
    for (const entry of words) {
      const group = byFirst.get(entry.word[0]) ?? [];
      group.push(entry.word);
      byFirst.set(entry.word[0], group);
    }
    return [...byFirst].sort((a, b) => a[0].localeCompare(b[0]));
  }, [words]);
  return (
    <div className="space-y-3">
      {groups.map(([letter, list]) => (
        <section className="space-y-1" key={letter}>
          <h4 className="font-medium text-foreground text-sm">
            {t(strings.firstLetter, { letter: letter.toUpperCase() })}
          </h4>
          <p className="font-mono text-foreground text-sm">{list.join("  ")}</p>
        </section>
      ))}
    </div>
  );
}

function Results({
  box,
  words,
  startWord,
  strings,
}: {
  box: Box;
  words: readonly BoxWord[];
  startWord: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const [wantThree, setWantThree] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const one = useMemo(
    () => runSearch(words, box, startWord, 1, TWO_LIMIT),
    [words, box, startWord]
  );
  const two = useMemo(
    () => runSearch(words, box, startWord, 2, TWO_LIMIT),
    [words, box, startWord]
  );
  const three = useMemo(
    () => (wantThree ? runSearch(words, box, startWord, 3, THREE_LIMIT) : null),
    [wantThree, words, box, startWord]
  );
  const top = useMemo(
    () =>
      [...words]
        .sort((a, b) => b.covers - a.covers || a.word.localeCompare(b.word))
        .slice(0, 12),
    [words]
  );
  return (
    <div aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.heading}</h2>
      <StatGrid>
        <StatCard label={strings.statValid} value={fmt(words.length)} />
        <StatCard label={strings.statOne} value={fmt(one.total)} />
        <StatCard
          emphasis
          label={strings.statTwo}
          value={`${fmt(two.total)}${two.truncated ? "+" : ""}`}
        />
      </StatGrid>
      <ChainList
        chains={one.chains}
        empty={strings.noOne}
        heading={strings.oneHeading}
        strings={strings}
        total={one.total}
        truncated={one.truncated}
      />
      <ChainList
        chains={two.chains}
        empty={strings.noTwo}
        heading={strings.twoHeading}
        strings={strings}
        total={two.total}
        truncated={two.truncated}
      />
      {three ? (
        <ChainList
          chains={three.chains}
          empty={strings.noThree}
          heading={strings.threeHeading}
          strings={strings}
          total={three.total}
          truncated={three.truncated}
        />
      ) : (
        <div className="space-y-2">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setWantThree(true)}
            type="button"
          >
            {strings.findThree}
          </button>
          <p className={HINT}>{strings.threeNote}</p>
        </div>
      )}
      <section className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.topHeading}</h3>
        <ul className="flex flex-wrap gap-2">
          {top.map((entry) => (
            <li
              className="rounded-md border border-border bg-card px-2.5 py-1 text-sm"
              key={entry.word}
            >
              <span className="font-mono text-foreground">{entry.word}</span>{" "}
              <span className={HINT}>
                {t(strings.covers, {
                  count: fmt(entry.covers),
                  total: fmt(box.size),
                })}
              </span>
            </li>
          ))}
        </ul>
      </section>
      <section className="space-y-2">
        <h3 className={WIDGET_H3}>
          {strings.allHeading} <span className={HINT}>{fmt(words.length)}</span>
        </h3>
        <button
          className={BUTTON_SMALL}
          onClick={() => setListOpen((open) => !open)}
          type="button"
        >
          {listOpen ? strings.hideWords : strings.showWords}
        </button>
        {listOpen ? <WordsByLetter strings={strings} words={words} /> : null}
      </section>
    </div>
  );
}

export default function LetterBoxedSolverWidget() {
  const strings = useToolUi(LETTER_BOXED_SOLVER_STRINGS);
  const fmt = useToolNumber();
  const [sides, setSides] = useState<string[]>(["", "", "", ""]);
  const [start, setStart] = useState("");
  const parsed = parseBox(sides);
  const { words, status, retry } = useWordList(parsed.ok);
  const sidesKey = sides.join("|");
  const valid = useMemo(() => {
    const box = parseBox(sidesKey.split("|"));
    return box.ok && words ? validWords(words, box.box) : null;
  }, [sidesKey, words]);
  const startWord = start.trim().toLowerCase();
  const startOk = valid?.some((entry) => entry.word === startWord) ?? false;

  function setSide(index: number, value: string) {
    setSides((current) =>
      current.map((side, at) =>
        at === index
          ? value
              .toLowerCase()
              .replace(RE_NON_LETTER, "")
              .slice(0, MAX_PER_SIDE + 2)
          : side
      )
    );
  }

  function notice() {
    if (parsed.ok) {
      return null;
    }
    if (parsed.reason === "empty") {
      return <Notice>{strings.emptyPrompt}</Notice>;
    }
    const message = t(strings[INVALID_KEYS[parsed.reason]], {
      max: fmt(MAX_PER_SIDE),
    });
    return parsed.reason === "incomplete" ? (
      <Notice>{message}</Notice>
    ) : (
      <ErrorNotice>{message}</ErrorNotice>
    );
  }

  return (
    <>
      <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-start">
        <div className="grid gap-4 sm:grid-cols-2">
          {SIDES.map((index) => (
            <TextField
              hint={t(strings.sideHint, { max: fmt(MAX_PER_SIDE) })}
              id={`boxed-side-${index}`}
              key={index}
              label={strings.sideLabels[index]}
              mono
              onChange={(value) => setSide(index, value)}
              placeholder={strings.sidePlaceholder}
              value={sides[index]}
            />
          ))}
        </div>
        <BoxPreview
          sides={parsed.ok ? parsed.box.sides : sides.map((s) => [...s])}
        />
      </div>
      <div className="grid items-end gap-4 sm:grid-cols-[2fr_1fr]">
        <TextField
          hint={strings.startHint}
          id="boxed-start"
          label={strings.startLabel}
          mono
          onChange={(value) => setStart(value.slice(0, 20))}
          placeholder={strings.startPlaceholder}
          value={start}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setSides(EXAMPLE);
            setStart("");
          }}
          type="button"
        >
          {strings.tryExample}
        </button>
      </div>
      {notice()}
      <WordListStatus
        error={strings.loadError}
        loading={strings.loading}
        onRetry={retry}
        retry={strings.retry}
        status={status}
      />
      {startWord && valid && !startOk ? (
        <ErrorNotice>
          {t(strings.startInvalid, { word: startWord })}
        </ErrorNotice>
      ) : null}
      {parsed.ok && valid ? (
        <Results
          box={parsed.box}
          startWord={startOk ? startWord : ""}
          strings={strings}
          words={valid}
        />
      ) : null}
      <p className={HINT}>{strings.sourceNote}</p>
    </>
  );
}
